/*
 * Copyright (c) 2023. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import {
  FlowRunState,
  type FlowRunStatus,
  type ManufacturerRecord,
  type SharepointFolder,
  type Workflow,
  type WorkflowMeta,
} from '@poweralpha/common';
import {
  type UseMutationOptions,
  type UseQueryOptions,
  type UseSuspenseQueryOptions,
  useQuery,
} from '@tanstack/react-query';
import { minutesToMilliseconds } from 'date-fns';
import type { HTTPError } from 'ky';
import { api, checkForStatus, getGisGeoJson, getManufacturers, getWorkflowResult } from '../api';

export type QueryOptions<TQueryFnData = unknown, Error = HTTPError, Data = TQueryFnData> = Omit<
  UseQueryOptions<TQueryFnData, Error, Data>,
  'queryKey'
>;

export type MutationOptions<TQueryFnData = unknown, Error = HTTPError, Data = void> = Omit<
  UseMutationOptions<TQueryFnData, Error, Data>,
  'mutationKey' | 'mutationFn'
>;

export type SuspenseQueryOptions<TData> = Omit<
  UseSuspenseQueryOptions<TData, HTTPError, TData, readonly unknown[]>,
  'queryFn' | 'queryKey'
>;

export const useStatusQuery = (
  runId: string | undefined | null,
  opts?: QueryOptions<FlowRunStatus>,
) =>
  useQuery<FlowRunStatus, HTTPError, FlowRunStatus>({
    queryKey: ['status', runId],
    queryFn: () => checkForStatus(runId),
    enabled: Boolean(runId) && (opts?.enabled ?? true),
    refetchInterval: (query) =>
      query.state.data?.state.type === FlowRunState.COMPLETED ? false : 5000,
    ...opts,
  });

export function useWorkflowQuery<T extends WorkflowMeta, Add = unknown>(
  id: string | undefined | null,
  opts?: QueryOptions<Workflow<T> & Add>,
) {
  return useQuery<Workflow<T> & Add>({
    queryKey: ['workflow', id],
    queryFn: () => getWorkflowResult<T, Add>(id),
    enabled: Boolean(id) && (opts?.enabled ?? true),
    ...opts,
  });
}

export function useMultipleWorkflowResultsQuery<T extends WorkflowMeta, Add = unknown>(
  ids: string[],
  opts?: QueryOptions<Array<Workflow<T> & Add>>,
) {
  return useQuery<Array<Workflow<T> & Add>>({
    queryKey: ['multiple-workflows', ids],
    queryFn: () =>
      api.get(`workflows/multiple-results`, { searchParams: { ids: ids.join(',') } }).json(),
    enabled: Boolean(ids?.length) && (opts?.enabled ?? true),
    staleTime: Number.POSITIVE_INFINITY,
    ...opts,
  });
}

export const getSharepointFolderQueryKey = (path: string | undefined) => [
  'sharepoint-folder',
  path,
];

export function useSharepointFolderQuery(
  path: string | undefined,
  opts?: {
    excludeSubfolders?: boolean;
    excludeFiles?: boolean;
    createFolders?: boolean;
  } & QueryOptions<SharepointFolder>,
) {
  return useQuery<SharepointFolder>({
    queryKey: getSharepointFolderQueryKey(path),
    queryFn: () =>
      api
        .get('sharepoint/list', {
          searchParams: {
            path: path ?? '',
            excludeSubfolders: opts?.excludeSubfolders,
            excludeFiles: opts?.excludeFiles,
            createFolders: opts?.createFolders,
          },
        })
        .json(),
    ...opts,
    enabled: (opts?.enabled ?? true) && Boolean(path),
  });
}

export function useGisGeojsonQuery<T>(
  id: string | undefined,
  filename: string,
  opts?: QueryOptions<T>,
) {
  return useQuery<T>({
    queryKey: [`gis-${filename}`, id],
    queryFn: () => getGisGeoJson<T>(id, filename),
    ...opts,
    staleTime: Number.POSITIVE_INFINITY,
    enabled: (opts?.enabled ?? true) && Boolean(id),
  });
}

export const useManufacturersQuery = (type: ManufacturerRecord['component_type']) =>
  useQuery({
    queryKey: [`${type}-manufacturers`],
    queryFn: () => getManufacturers(type),
    staleTime: minutesToMilliseconds(60 * 24), // 24 hrs
  });
